import { useState, useEffect } from "react";
import { Menu, X, Globe } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link, useLocation, useNavigate } from "react-router-dom";

const NAV_KEYS = [
  { key: "services", href: "#services" },
  { key: "fleet", href: "#fleet" },
  { key: "corporate", href: "#corporate" },
  { key: "contact", href: "#footer" },
];

const EXPERIENCE_LABEL = {
  en: "Experience",
  ar: "التجارب",
  uz: "Tajribalar",
  ru: "Путешествия",
};

const WHATSAPP_LINK =
  "https://wa.me/966540423401?text=Hello%20BlackRide%20%F0%9F%9A%98%0A%0AI%E2%80%99d%20like%20to%20book%20a%20ride.%0A%0APickup%3A%0ADrop%3A%0ADate%20%26%20Time%3A%0APassengers%3A%0A%0APlease%20confirm%20availability.";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const { t, setLang, lang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const languageOptions = [
    { code: "en", label: "English" },
    { code: "ar", label: "العربية" },
    { code: "uz", label: "O'zbek" },
    { code: "ru", label: "Русский" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setLanguageOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickAway = (event) => {
      if (!(event.target instanceof Element) || !event.target.closest(".header-lang-toggle")) {
        setLanguageOpen(false);
      }
    };

    document.addEventListener("click", handleClickAway);
    return () => document.removeEventListener("click", handleClickAway);
  }, []);

  const scrollTo = (href) => {
    setMobileOpen(false);

    if (location.pathname !== "/") {
      navigate(`/${href}`);
      return;
    }

    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 header-shell ${
        scrolled
          ? "backdrop-blur-xl bg-black/80 border-b border-white/10 shadow-[0_2px_30px_rgba(0,0,0,0.6)] header-shell--scrolled"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between py-5">
        <Link to="/" className="flex items-center flex-shrink-0" onClick={() => setMobileOpen(false)}>
          <img
            src="/logo-white.png"
            alt="BlackRide Logo"
            className="h-8 md:h-10 w-auto object-contain header-logo-mark"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {NAV_KEYS.map((link) => (
            <button
              key={link.href}
              onClick={() => scrollTo(link.href)}
              className="text-sm tracking-wide text-gray-300 hover:text-white transition-all duration-300 header-nav-link"
            >
              {t(link.key)}
            </button>
          ))}
          <Link
            to="/experience"
            className="text-sm tracking-wide text-gray-300 hover:text-white transition-all duration-300 header-nav-link"
          >
            {EXPERIENCE_LABEL[lang] || EXPERIENCE_LABEL.en}
          </Link>
        </nav>

        <div className="hidden md:flex items-center gap-6">
          <div className="relative header-lang-toggle">
            <button
              onClick={() => setLanguageOpen((current) => !current)}
              className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition header-lang-toggle"
              aria-haspopup="menu"
              aria-expanded={languageOpen}
            >
              <Globe className="w-4 h-4" />
              <span>{languageOptions.find((option) => option.code === lang)?.label || "English"}</span>
            </button>

            <AnimatePresence>
              {languageOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  className="absolute right-0 top-full mt-3 min-w-[10rem] overflow-hidden rounded-2xl border border-white/10 bg-black/95 p-2 shadow-[0_18px_60px_rgba(0,0,0,0.45)]"
                >
                  {languageOptions.map((option) => (
                    <button
                      key={option.code}
                      type="button"
                      onClick={() => {
                        setLang(option.code);
                        setLanguageOpen(false);
                      }}
                      className={`flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-sm transition ${
                        lang === option.code ? "bg-[#C7A239]/10 text-[#E0C26A]" : "text-gray-300 hover:bg-white/5 hover:text-white"
                      }`}
                      role="menuitem"
                    >
                      <span>{option.label}</span>
                      {lang === option.code ? <span className="text-[10px] uppercase tracking-[0.2em]">Live</span> : null}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noreferrer"
            className="bg-[#C7A239] hover:bg-[#D4B34A] text-black font-semibold px-7 py-3 rounded-full transition-all duration-300 shadow-lg hover:shadow-[#C7A239]/30"
            data-testid="header-whatsapp-cta"
            data-track="header_whatsapp_cta"
          >
            {t("bookNow")}
          </a>
        </div>

        <div className="flex md:hidden items-center gap-3">
          <div className="relative header-lang-toggle">
            <button
              onClick={() => setLanguageOpen((current) => !current)}
              className="text-gray-400 hover:text-white p-1.5 header-lang-toggle"
              aria-haspopup="menu"
              aria-expanded={languageOpen}
            >
              <Globe className="w-5 h-5" />
            </button>

            <AnimatePresence>
              {languageOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  className="absolute right-0 top-full mt-3 min-w-[9rem] overflow-hidden rounded-2xl border border-white/10 bg-black/95 p-2 shadow-[0_18px_60px_rgba(0,0,0,0.45)]"
                >
                  {languageOptions.map((option) => (
                    <button
                      key={option.code}
                      type="button"
                      onClick={() => {
                        setLang(option.code);
                        setLanguageOpen(false);
                        setMobileOpen(false);
                      }}
                      className={`block w-full rounded-xl px-3 py-2.5 text-left text-sm transition ${
                        lang === option.code ? "bg-[#C7A239]/10 text-[#E0C26A]" : "text-gray-300 hover:bg-white/5 hover:text-white"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <button className="text-white p-2 header-menu-button" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-black/95 border-b border-white/10 header-mobile-panel"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV_KEYS.map((link) => (
                <button
                  key={link.href}
                  onClick={() => scrollTo(link.href)}
                  className="text-left text-gray-300 hover:text-white header-nav-link"
                >
                  {t(link.key)}
                </button>
              ))}

              <Link
                to="/experience"
                onClick={() => setMobileOpen(false)}
                className="text-left text-gray-300 hover:text-white header-nav-link"
              >
                {EXPERIENCE_LABEL[lang] || EXPERIENCE_LABEL.en}
              </Link>

              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noreferrer"
                onClick={() => setMobileOpen(false)}
                className="bg-[#C7A239] text-black font-medium px-6 py-3 rounded-full text-center"
              >
                {t("bookNow")}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
